// ============================================================================
// RELATIONS ENGINE - Dynamic World State
// ============================================================================

// CONSTANTS (rules, not state)
const RELATIONS_MIN = -100;
const RELATIONS_MAX = 100;

// THRESHOLDS
const RELATIONS_THRESHOLDS = {
  HOSTILE: -50,
  COLD: -20,
  NEUTRAL: 10,
  WARM: 40,
  FRIENDLY: 70,
  PARTNERSHIP: 85,
  ALLIANCE: 100
};

// NEGOTIATION RESPONSES
const NEGOTIATION_RESPONSES = {
  LOCKED_OUT: { min: -100, max: -50, message: "We are not accepting applications from your airline.", fee: null, waitDays: 90 },
  COLD: { min: -50, max: -20, message: "Check back in 60 days.", fee: null, waitDays: 60 },
  PREMIUM: { min: -20, max: 10, message: "Limited availability. Premium terms.", fee: 100000, waitDays: 0 },
  STANDARD: { min: 10, max: 40, message: "Standard negotiation available.", fee: 50000, waitDays: 0 },
  REDUCED: { min: 40, max: 70, message: "We'd welcome your service.", fee: 25000, waitDays: 0 },
  PRIORITY: { min: 70, max: 85, message: "Priority access granted.", fee: 0, waitDays: 0 },
  PARTNERSHIP: { min: 85, max: 100, message: "We'd like to discuss a deeper partnership.", fee: 0, waitDays: 0, allianceEligible: true }
};

// RELATIONS DELTAS - AIRPORTS
const AIRPORT_RELATIONS_EVENTS = {
  ON_TIME_FLIGHT: 0.5,
  CANCELLED_FLIGHT: -5,
  DELAY_30_PLUS: -1,
  DELAY_90_PLUS: -2,
  NEGOTIATION_WIN: 3,
  NEGOTIATION_LOSS: -2,
  AD_CAMPAIGN: 2,
  REBRAND: 'RESET_NEGATIVES',
  NEW_ROUTE_THEY_WANTED: 15,
  PASSENGER_COMPLAINT: -3,
  SAFETY_INCIDENT: -10,
  CREW_LEGALITY_BUST: -5
};

// RELATIONS DELTAS - COMPETITORS
const COMPETITOR_RELATIONS_EVENTS = {
  BEAT_IN_NEGOTIATION: -10,
  LOST_TO_IN_NEGOTIATION: -5,
  UNDERCUT_PRICING: -3,
  MATCH_PRICING: 0,
  PREMIUM_OVER_PRICING: 2,
  STOLE_MARKET_SHARE_20: -15,
  EXITED_ROUTE_THEY_WANT: 10,
  ALLIANCE_WITH_THEIR_ENEMY: -20,
  HELPED_IN_CRISIS: 15,
  CODESHARE_AGREEMENT: 25
};

// ALLIANCE BENEFITS
const ALLIANCE_BENEFITS = {
  CODESHARE: { name: "Codeshare", description: "Your flights show partner flight numbers, +15% seat fill" },
  SHARED_GATES: { name: "Shared Gates", description: "Use partner gates at their hub, no slot negotiation" },
  CREW_POOLING: { name: "Crew Pooling", description: "Borrow partner crew in emergencies" },
  MAINTENANCE_NETWORK: { name: "Maintenance Network", description: "20% discount at partner MRO facilities" },
  JOINT_PURCHASING: { name: "Joint Purchasing", description: "10% aircraft lease discount" },
  REVENUE_SHARE: { name: "Revenue Share", description: "5% of connecting passenger revenue" }
};

// ============================================================================
// TEMPLATES (starting values - will be copied into live state)
// ============================================================================

const COMPETITOR_TEMPLATES = {
  NORTHREACH: {
    id: "NORTHREACH",
    name: "NorthReach Air",
    hub: "KSEA",
    aggression: 6,
    style: "Regional gatekeeper",
    behavior: "Defends short-haul routes, resists newcomers",
    startingSlots: { KSEA: 8, KPDX: 4, KGEG: 3, KBOI: 3 },
    startingCash: 80000000,
    startingHealth: 85,
    startingRoutes: ["KSEA-KPDX", "KSEA-KGEG", "KSEA-KBOI", "KPDX-KBOI"]
  },
  CASCADE: {
    id: "CASCADE",
    name: "Cascade Wings",
    hub: "KPDX",
    aggression: 4,
    style: "Friendly regional incumbent",
    behavior: "Cooperative early, competitive if undercut",
    startingSlots: { KPDX: 6, KEUG: 3, KMFR: 2 },
    startingCash: 45000000,
    startingHealth: 70,
    startingRoutes: ["KPDX-KEUG", "KPDX-KMFR", "KEUG-KMFR"]
  },
  COPPER_RIDGE: {
    id: "COPPER_RIDGE",
    name: "Copper Ridge Air",
    hub: "KBZN",
    aggression: 9,
    style: "Territorial regional bully",
    behavior: "Aggressive slot blocking, hostile negotiations",
    startingSlots: { KBZN: 5, KIDA: 3, KJAC: 4 },
    startingCash: 35000000,
    startingHealth: 65,
    startingRoutes: ["KBZN-KIDA", "KBZN-KJAC", "KJAC-KIDA"]
  },
  HIGHPLAINS: {
    id: "HIGHPLAINS",
    name: "HighPlains Airways",
    hub: "KDEN",
    aggression: 8,
    style: "Opportunistic challenger",
    behavior: "Price wars, fast expansion",
    startingSlots: { KDEN: 10, KCOS: 4, KASE: 3 },
    startingCash: 120000000,
    startingHealth: 90,
    startingRoutes: ["KDEN-KCOS", "KDEN-KASE", "KDEN-KSLC"]
  },
  UNION_SKIES: {
    id: "UNION_SKIES",
    name: "Union Skies",
    hub: "KORD",
    aggression: 6,
    style: "Strategic legacy-style",
    behavior: "Uses influence more than pricing",
    startingSlots: { KORD: 12, KMSP: 6, KDTW: 5 },
    startingCash: 200000000,
    startingHealth: 80,
    startingRoutes: ["KORD-KMSP", "KORD-KDTW", "KMSP-KDTW"]
  },
  ATLAS_CROWN: {
    id: "ATLAS_CROWN",
    name: "Atlas Crown",
    hub: "KATL",
    aggression: 5,
    style: "Institutional heavyweight",
    behavior: "Political pressure, slow retaliation",
    startingSlots: { KATL: 15, KBNA: 5, KCLT: 8 },
    startingCash: 300000000,
    startingHealth: 95,
    startingRoutes: ["KATL-KBNA", "KATL-KCLT", "KBNA-KCLT"]
  },
  SUNREACH: {
    id: "SUNREACH",
    name: "SunReach Airlines",
    hub: "KPHX",
    aggression: 7,
    style: "Leisure-focused",
    behavior: "Seasonal surges, price-sensitive",
    startingSlots: { KPHX: 8, KLAX: 5, KSAN: 4 },
    startingCash: 90000000,
    startingHealth: 75,
    startingRoutes: ["KPHX-KLAX", "KPHX-KSAN", "KLAX-KSAN"]
  },
  FRONTLINE: {
    id: "FRONTLINE",
    name: "Frontline Aero",
    hub: "KLAS",
    aggression: 7,
    style: "High-frequency short-haul",
    behavior: "Slot saturation, fast turnarounds",
    startingSlots: { KLAS: 10, KRNO: 4, KSLC: 5 },
    startingCash: 75000000,
    startingHealth: 80,
    startingRoutes: ["KLAS-KRNO", "KLAS-KSLC", "KRNO-KSLC"]
  },
  IRONWAVE: {
    id: "IRONWAVE",
    name: "IronWave Air",
    hub: "KSFO",
    aggression: 6,
    style: "Premium west-coast",
    behavior: "Defends premium routes fiercely",
    startingSlots: { KSFO: 10, KOAK: 4, KSJC: 4 },
    startingCash: 150000000,
    startingHealth: 85,
    startingRoutes: ["KSFO-KOAK", "KSFO-KSJC", "KOAK-KSJC"]
  },
  POLAR_MERIDIAN: {
    id: "POLAR_MERIDIAN",
    name: "Polar Meridian",
    hub: "PANC",
    aggression: 5,
    style: "Long-haul / extreme ops",
    behavior: "Weather-resistant, niche dominance",
    startingSlots: { PANC: 6, PAFA: 3, PADQ: 2 },
    startingCash: 60000000,
    startingHealth: 70,
    startingRoutes: ["PANC-PAFA", "PANC-PADQ"]
  }
};

const AIRPORT_TEMPLATES = {
  // CORE PNW
  KPDX: { name: "Portland Intl", type: "major_regional", baseDemand: 1.8, totalSlots: 20, startingLoyalty: { CASCADE: 50, NORTHREACH: 20 }, playerStart: 10 },
  KSEA: { name: "Seattle-Tacoma", type: "major_hub", baseDemand: 2.2, totalSlots: 30, startingLoyalty: { NORTHREACH: 70 }, playerStart: 0 },
  KGEG: { name: "Spokane Intl", type: "regional", baseDemand: 1.1, totalSlots: 12, startingLoyalty: { NORTHREACH: 40 }, playerStart: 5 },
  KEUG: { name: "Eugene", type: "regional", baseDemand: 0.9, totalSlots: 8, startingLoyalty: { CASCADE: 30 }, playerStart: 10 },
  KMFR: { name: "Medford", type: "regional", baseDemand: 0.8, totalSlots: 6, startingLoyalty: { CASCADE: 25 }, playerStart: 10 },
  KBOI: { name: "Boise", type: "regional", baseDemand: 1.1, totalSlots: 12, startingLoyalty: { NORTHREACH: 35 }, playerStart: 5 },
  KRDM: { name: "Redmond", type: "small_regional", baseDemand: 0.6, totalSlots: 6, startingLoyalty: {}, playerStart: 15 },

  // MOUNTAIN WEST
  KBZN: { name: "Bozeman", type: "regional", baseDemand: 0.9, totalSlots: 10, startingLoyalty: { COPPER_RIDGE: 60 }, playerStart: -10 },
  KJAC: { name: "Jackson Hole", type: "regional", baseDemand: 0.8, totalSlots: 8, startingLoyalty: { COPPER_RIDGE: 50 }, playerStart: -5 },
  KIDA: { name: "Idaho Falls", type: "small_regional", baseDemand: 0.6, totalSlots: 6, startingLoyalty: { COPPER_RIDGE: 40 }, playerStart: 0 },
  KSLC: { name: "Salt Lake City", type: "mid_hub", baseDemand: 1.5, totalSlots: 25, startingLoyalty: { FRONTLINE: 40 }, playerStart: 0 },
  KRNO: { name: "Reno-Tahoe", type: "regional", baseDemand: 1.0, totalSlots: 12, startingLoyalty: { FRONTLINE: 30 }, playerStart: 5 },

  // CALIFORNIA
  KLAX: { name: "Los Angeles Intl", type: "mega_hub", baseDemand: 2.5, totalSlots: 50, startingLoyalty: { SUNREACH: 50 }, playerStart: -5 },
  KSAN: { name: "San Diego Intl", type: "regional_intl", baseDemand: 1.6, totalSlots: 20, startingLoyalty: { SUNREACH: 40 }, playerStart: 5 },
  KSFO: { name: "San Francisco Intl", type: "major_hub", baseDemand: 2.3, totalSlots: 35, startingLoyalty: { IRONWAVE: 60 }, playerStart: -5 },
  KOAK: { name: "Oakland Intl", type: "secondary_hub", baseDemand: 1.7, totalSlots: 18, startingLoyalty: { IRONWAVE: 35 }, playerStart: 10 },
  KSJC: { name: "San Jose Intl", type: "regional", baseDemand: 1.2, totalSlots: 15, startingLoyalty: { IRONWAVE: 30 }, playerStart: 10 },
  KSMF: { name: "Sacramento Intl", type: "regional", baseDemand: 1.1, totalSlots: 14, startingLoyalty: {}, playerStart: 10 },

  // CENTRAL / MIDWEST
  KDEN: { name: "Denver Intl", type: "major_hub", baseDemand: 2.0, totalSlots: 40, startingLoyalty: { HIGHPLAINS: 65 }, playerStart: 0 },
  KCOS: { name: "Colorado Springs", type: "regional", baseDemand: 0.9, totalSlots: 10, startingLoyalty: { HIGHPLAINS: 40 }, playerStart: 5 },
  KASE: { name: "Aspen", type: "regional", baseDemand: 0.8, totalSlots: 6, startingLoyalty: { HIGHPLAINS: 35 }, playerStart: 0 },
  KORD: { name: "Chicago O'Hare", type: "mega_hub", baseDemand: 2.6, totalSlots: 60, startingLoyalty: { UNION_SKIES: 60 }, playerStart: -10 },
  KMSP: { name: "Minneapolis-St Paul", type: "major_hub", baseDemand: 1.9, totalSlots: 30, startingLoyalty: { UNION_SKIES: 40 }, playerStart: 5 },

  // SOUTH / EAST
  KATL: { name: "Atlanta Intl", type: "mega_hub", baseDemand: 2.7, totalSlots: 70, startingLoyalty: { ATLAS_CROWN: 70 }, playerStart: -10 },
  KBNA: { name: "Nashville Intl", type: "regional_hub", baseDemand: 1.4, totalSlots: 18, startingLoyalty: { ATLAS_CROWN: 40 }, playerStart: 5 },
  KCLT: { name: "Charlotte Douglas", type: "major_hub", baseDemand: 1.8, totalSlots: 28, startingLoyalty: { ATLAS_CROWN: 50 }, playerStart: 0 },
  KDTW: { name: "Detroit Metro", type: "major_hub", baseDemand: 1.7, totalSlots: 25, startingLoyalty: { UNION_SKIES: 30 }, playerStart: 5 },

  // ALASKA
  PANC: { name: "Anchorage Intl", type: "intl_hub", baseDemand: 1.3, totalSlots: 15, startingLoyalty: { POLAR_MERIDIAN: 60 }, playerStart: 0 },
  PAFA: { name: "Fairbanks Intl", type: "regional", baseDemand: 0.7, totalSlots: 8, startingLoyalty: { POLAR_MERIDIAN: 40 }, playerStart: 5 },
  PADQ: { name: "Kodiak", type: "regional", baseDemand: 0.6, totalSlots: 4, startingLoyalty: { POLAR_MERIDIAN: 35 }, playerStart: 5 },

  // EXPANSION
  KPHX: { name: "Phoenix Sky Harbor", type: "major_hub", baseDemand: 1.9, totalSlots: 35, startingLoyalty: { SUNREACH: 55 }, playerStart: 0 },
  KLAS: { name: "Las Vegas McCarran", type: "major_leisure", baseDemand: 2.1, totalSlots: 40, startingLoyalty: { FRONTLINE: 50 }, playerStart: 5 },
  KEWR: { name: "Newark Liberty", type: "intl_hub", baseDemand: 2.4, totalSlots: 45, startingLoyalty: { UNION_SKIES: 40 }, playerStart: -5 }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

// ============================================================================
// STATE BUILDERS
// ============================================================================

function buildLiveCompetitors() {
  const competitors = {};

  for (const [id, template] of Object.entries(COMPETITOR_TEMPLATES)) {
    competitors[id] = {
      id: template.id,
      name: template.name,
      hub: template.hub,
      aggression: template.aggression,
      style: template.style,
      behavior: template.behavior,
      // Mutable properties
      slots: { ...template.startingSlots },
      cash: template.startingCash,
      health: template.startingHealth,
      expanding: false,
      routes: [...template.startingRoutes],
      // Status
      isActive: true,
      bankruptAt: null,
      mergedInto: null
    };
  }

  return competitors;
}

function buildLiveAirports() {
  const airports = {};

  for (const [code, template] of Object.entries(AIRPORT_TEMPLATES)) {
    airports[code] = {
      code,
      name: template.name,
      type: template.type,
      // Mutable properties
      loyalty: { ...template.startingLoyalty },
      totalSlots: template.totalSlots,
      currentDemand: template.baseDemand,
      baseDemand: template.baseDemand,
      congestion: 0, // 0-100, affects delay probability
      // Infrastructure
      expansionCost: template.totalSlots * 500000, // Cost to add more slots
      lastExpansion: null
    };
  }

  return airports;
}

function buildPlayerRelations() {
  const airportRelations = {};
  const competitorRelations = {};

  // Initialize airport relations from templates
  for (const [code, template] of Object.entries(AIRPORT_TEMPLATES)) {
    airportRelations[code] = {
      base: template.playerStart,
      current: template.playerStart,
      history: [],
      lastContact: null,
      waitUntil: null
    };
  }

  // Initialize competitor relations (all start neutral)
  for (const id of Object.keys(COMPETITOR_TEMPLATES)) {
    competitorRelations[id] = {
      base: 0,
      current: 0,
      history: [],
      isAllied: false,
      allianceSince: null
    };
  }

  return { airports: airportRelations, competitors: competitorRelations };
}

// ============================================================================
// CREATE RELATIONS STATE
// ============================================================================

export function createRelationsState() {
  return {
    // Live mutable state
    competitors: buildLiveCompetitors(),
    airports: buildLiveAirports(),
    playerRelations: buildPlayerRelations(),

    // Alliances and negotiation
    alliances: [],
    negotiator: null,
    rebrands: 0,

    // World event log
    worldEvents: []
  };
}

// ============================================================================
// WORLD MUTATION FUNCTIONS
// ============================================================================

/**
 * Competitor gains slots at an airport
 */
export function competitorExpandsTo(state, competitorId, airportCode, slotCount = 1) {
  const newState = deepClone(state);
  const competitor = newState.competitors[competitorId];
  const airport = newState.airports[airportCode];

  if (!competitor || !airport || !competitor.isActive) return state;

  // Check if slots available
  const currentUsed = Object.values(newState.competitors)
    .filter(c => c.isActive)
    .reduce((sum, c) => sum + (c.slots[airportCode] || 0), 0);

  const playerSlots = 0; // Would need to be passed in or calculated
  const available = airport.totalSlots - currentUsed - playerSlots;

  if (available < slotCount) return state;

  // Add slots
  competitor.slots[airportCode] = (competitor.slots[airportCode] || 0) + slotCount;
  competitor.expanding = true;
  competitor.cash -= slotCount * 100000; // Slot acquisition cost

  // Increase airport loyalty to this competitor
  airport.loyalty[competitorId] = (airport.loyalty[competitorId] || 0) + (slotCount * 2);

  // Log event
  newState.worldEvents.push({
    type: 'COMPETITOR_EXPANSION',
    competitorId,
    airportCode,
    slotCount,
    timestamp: new Date().toISOString()
  });

  return newState;
}

/**
 * Competitor loses slots at an airport
 */
export function competitorLosesSlot(state, competitorId, airportCode, slotCount = 1) {
  const newState = deepClone(state);
  const competitor = newState.competitors[competitorId];

  if (!competitor || !competitor.isActive) return state;

  const currentSlots = competitor.slots[airportCode] || 0;
  const toLose = Math.min(slotCount, currentSlots);

  if (toLose <= 0) return state;

  competitor.slots[airportCode] = currentSlots - toLose;
  if (competitor.slots[airportCode] === 0) {
    delete competitor.slots[airportCode];
  }

  // Decrease airport loyalty
  const airport = newState.airports[airportCode];
  if (airport && airport.loyalty[competitorId]) {
    airport.loyalty[competitorId] = Math.max(0, airport.loyalty[competitorId] - (toLose * 3));
  }

  // Log event
  newState.worldEvents.push({
    type: 'COMPETITOR_SLOT_LOSS',
    competitorId,
    airportCode,
    slotCount: toLose,
    timestamp: new Date().toISOString()
  });

  return newState;
}

/**
 * Competitor goes bankrupt - removed from market, slots redistributed
 */
export function competitorGoesBankrupt(state, competitorId) {
  const newState = deepClone(state);
  const competitor = newState.competitors[competitorId];

  if (!competitor || !competitor.isActive) return state;

  // Mark as inactive
  competitor.isActive = false;
  competitor.bankruptAt = new Date().toISOString();
  competitor.health = 0;

  // Redistribute slots to remaining competitors (or leave vacant)
  const vacatedSlots = { ...competitor.slots };
  competitor.slots = {};
  competitor.routes = [];

  // For each airport, reduce loyalty and potentially redistribute
  for (const [airportCode, slotCount] of Object.entries(vacatedSlots)) {
    const airport = newState.airports[airportCode];
    if (airport) {
      delete airport.loyalty[competitorId];
      // Slots become available (not auto-redistributed - opportunity for player!)
    }
  }

  // Log event
  newState.worldEvents.push({
    type: 'COMPETITOR_BANKRUPTCY',
    competitorId,
    competitorName: competitor.name,
    vacatedSlots,
    timestamp: new Date().toISOString()
  });

  return newState;
}

/**
 * New competitor enters the market
 */
export function newCompetitorEnters(state, competitorData) {
  const newState = deepClone(state);

  const id = competitorData.id || `NEW_${Date.now()}`;

  newState.competitors[id] = {
    id,
    name: competitorData.name || "New Airways",
    hub: competitorData.hub,
    aggression: competitorData.aggression || 5,
    style: competitorData.style || "New entrant",
    behavior: competitorData.behavior || "Aggressive growth phase",
    slots: competitorData.slots || {},
    cash: competitorData.cash || 50000000,
    health: competitorData.health || 60,
    expanding: true,
    routes: competitorData.routes || [],
    isActive: true,
    bankruptAt: null,
    mergedInto: null
  };

  // Initialize player relations with new competitor
  newState.playerRelations.competitors[id] = {
    base: 0,
    current: 0,
    history: [],
    isAllied: false,
    allianceSince: null
  };

  // Log event
  newState.worldEvents.push({
    type: 'NEW_COMPETITOR',
    competitorId: id,
    competitorName: newState.competitors[id].name,
    hub: competitorData.hub,
    timestamp: new Date().toISOString()
  });

  return newState;
}

/**
 * Airport shifts loyalty from one competitor to another
 */
export function airportShiftsLoyalty(state, airportCode, toCompetitorId, amount) {
  const newState = deepClone(state);
  const airport = newState.airports[airportCode];

  if (!airport) return state;

  // Increase loyalty to target
  airport.loyalty[toCompetitorId] = Math.min(100, (airport.loyalty[toCompetitorId] || 0) + amount);

  // Proportionally decrease loyalty to others
  const otherCompetitors = Object.keys(airport.loyalty).filter(id => id !== toCompetitorId);
  const decreaseEach = Math.ceil(amount / Math.max(1, otherCompetitors.length));

  for (const otherId of otherCompetitors) {
    airport.loyalty[otherId] = Math.max(0, airport.loyalty[otherId] - decreaseEach);
    if (airport.loyalty[otherId] === 0) {
      delete airport.loyalty[otherId];
    }
  }

  // Log event
  newState.worldEvents.push({
    type: 'LOYALTY_SHIFT',
    airportCode,
    toCompetitorId,
    amount,
    timestamp: new Date().toISOString()
  });

  return newState;
}

/**
 * Update airport demand (economy/seasonal shifts)
 */
export function updateAirportDemand(state, airportCode, newDemand) {
  const newState = deepClone(state);
  const airport = newState.airports[airportCode];

  if (!airport) return state;

  const oldDemand = airport.currentDemand;
  airport.currentDemand = Math.max(0.1, newDemand);

  // Log significant changes
  if (Math.abs(newDemand - oldDemand) > 0.2) {
    newState.worldEvents.push({
      type: 'DEMAND_SHIFT',
      airportCode,
      oldDemand,
      newDemand: airport.currentDemand,
      timestamp: new Date().toISOString()
    });
  }

  return newState;
}

/**
 * Competitor merger/acquisition
 */
export function competitorMerge(state, acquirerId, targetId) {
  const newState = deepClone(state);
  const acquirer = newState.competitors[acquirerId];
  const target = newState.competitors[targetId];

  if (!acquirer || !target || !acquirer.isActive || !target.isActive) return state;

  // Transfer slots
  for (const [airportCode, slotCount] of Object.entries(target.slots)) {
    acquirer.slots[airportCode] = (acquirer.slots[airportCode] || 0) + slotCount;
  }

  // Transfer routes
  acquirer.routes = [...new Set([...acquirer.routes, ...target.routes])];

  // Combine finances (acquirer pays, but gets assets)
  acquirer.cash = acquirer.cash - (target.cash * 0.5) + target.cash; // Net gain
  acquirer.health = Math.min(100, acquirer.health + 10); // Merger boost

  // Deactivate target
  target.isActive = false;
  target.mergedInto = acquirerId;
  target.slots = {};
  target.routes = [];

  // Transfer airport loyalties
  for (const [airportCode, airport] of Object.entries(newState.airports)) {
    if (airport.loyalty[targetId]) {
      airport.loyalty[acquirerId] = (airport.loyalty[acquirerId] || 0) + Math.floor(airport.loyalty[targetId] * 0.7);
      delete airport.loyalty[targetId];
    }
  }

  // Log event
  newState.worldEvents.push({
    type: 'COMPETITOR_MERGER',
    acquirerId,
    acquirerName: acquirer.name,
    targetId,
    targetName: target.name,
    timestamp: new Date().toISOString()
  });

  return newState;
}

/**
 * Update competitor health (called periodically)
 */
export function updateCompetitorHealth(state, competitorId, healthChange, reason = "") {
  const newState = deepClone(state);
  const competitor = newState.competitors[competitorId];

  if (!competitor || !competitor.isActive) return state;

  competitor.health = clamp(competitor.health + healthChange, 0, 100);

  // Check for bankruptcy
  if (competitor.health <= 0) {
    return competitorGoesBankrupt(newState, competitorId);
  }

  // Update expansion status based on health
  competitor.expanding = competitor.health > 60 && competitor.cash > 20000000;

  return newState;
}

/**
 * Expand airport infrastructure (add slots)
 */
export function expandAirport(state, airportCode, additionalSlots = 5) {
  const newState = deepClone(state);
  const airport = newState.airports[airportCode];

  if (!airport) return state;

  airport.totalSlots += additionalSlots;
  airport.expansionCost = airport.totalSlots * 500000;
  airport.lastExpansion = new Date().toISOString();

  // Log event
  newState.worldEvents.push({
    type: 'AIRPORT_EXPANSION',
    airportCode,
    additionalSlots,
    newTotal: airport.totalSlots,
    timestamp: new Date().toISOString()
  });

  return newState;
}

/**
 * Update airport congestion
 */
export function updateAirportCongestion(state, airportCode, congestionLevel) {
  const newState = deepClone(state);
  const airport = newState.airports[airportCode];

  if (!airport) return state;

  airport.congestion = clamp(congestionLevel, 0, 100);

  return newState;
}

// ============================================================================
// PLAYER RELATIONS FUNCTIONS (existing logic, updated for new structure)
// ============================================================================

/**
 * Calculate effective relations with an airport (includes competitor triangulation)
 */
export function getEffectiveAirportRelations(state, airportCode) {
  const airport = state.airports[airportCode];
  const playerRel = state.playerRelations.airports[airportCode];

  if (!airport || !playerRel) return 0;

  const baseRelations = playerRel.current;

  // Calculate competitor penalty (triangulation)
  let competitorPenalty = 0;
  for (const [competitorId, loyalty] of Object.entries(airport.loyalty || {})) {
    const competitor = state.competitors[competitorId];
    if (!competitor || !competitor.isActive) continue;

    const slotsHeld = competitor.slots[airportCode] || 0;
    const influence = slotsHeld / airport.totalSlots;
    competitorPenalty += loyalty * influence * 0.3;
  }

  // Negotiator bonus
  const negotiatorBonus = state.negotiator?.hired ? state.negotiator.bonus : 0;

  return clamp(baseRelations - competitorPenalty + negotiatorBonus, RELATIONS_MIN, RELATIONS_MAX);
}

/**
 * Get negotiation response based on effective relations
 */
export function getNegotiationResponse(effectiveRelations) {
  for (const [key, response] of Object.entries(NEGOTIATION_RESPONSES)) {
    if (effectiveRelations >= response.min && effectiveRelations < response.max) {
      return { type: key, ...response };
    }
  }
  return { type: 'LOCKED_OUT', ...NEGOTIATION_RESPONSES.LOCKED_OUT };
}

/**
 * Record a relations event with an airport
 */
export function recordAirportRelationsEvent(state, airportCode, eventType, details = {}) {
  const delta = AIRPORT_RELATIONS_EVENTS[eventType];
  if (delta === undefined) return state;

  const newState = deepClone(state);
  const airportRel = newState.playerRelations.airports[airportCode];

  if (!airportRel) return state;

  if (delta === 'RESET_NEGATIVES') {
    airportRel.current = Math.max(0, airportRel.current);
  } else {
    airportRel.current = clamp(airportRel.current + delta, RELATIONS_MIN, RELATIONS_MAX);
  }

  airportRel.history.push({
    event: eventType,
    delta: delta === 'RESET_NEGATIVES' ? 'reset' : delta,
    timestamp: new Date().toISOString(),
    details
  });

  if (airportRel.history.length > 50) {
    airportRel.history = airportRel.history.slice(-50);
  }

  return newState;
}

/**
 * Record a relations event with a competitor
 */
export function recordCompetitorRelationsEvent(state, competitorId, eventType, details = {}) {
  const delta = COMPETITOR_RELATIONS_EVENTS[eventType];
  if (delta === undefined) return state;

  const newState = deepClone(state);
  const compRel = newState.playerRelations.competitors[competitorId];

  if (!compRel) return state;

  compRel.current = clamp(compRel.current + delta, RELATIONS_MIN, RELATIONS_MAX);

  compRel.history.push({
    event: eventType,
    delta,
    timestamp: new Date().toISOString(),
    details
  });

  if (compRel.history.length > 50) {
    compRel.history = compRel.history.slice(-50);
  }

  return newState;
}

/**
 * Check if alliance is possible with a competitor
 */
export function canFormAlliance(state, competitorId) {
  const competitor = state.competitors[competitorId];
  const rel = state.playerRelations.competitors[competitorId];

  if (!competitor || !rel) return { canForm: false, reason: "Unknown competitor" };
  if (!competitor.isActive) return { canForm: false, reason: "Competitor no longer active" };
  if (rel.isAllied) return { canForm: false, reason: "Already allied" };
  if (rel.current < RELATIONS_THRESHOLDS.PARTNERSHIP) {
    return {
      canForm: false,
      reason: `Relations too low (${rel.current}/${RELATIONS_THRESHOLDS.PARTNERSHIP} needed)`
    };
  }

  return { canForm: true, reason: null };
}

/**
 * Form alliance with a competitor
 */
export function formAlliance(state, competitorId) {
  const check = canFormAlliance(state, competitorId);
  if (!check.canForm) return { success: false, ...check, state };

  const newState = deepClone(state);
  const compRel = newState.playerRelations.competitors[competitorId];
  const competitor = newState.competitors[competitorId];

  compRel.isAllied = true;
  compRel.allianceSince = new Date().toISOString();
  compRel.current = RELATIONS_MAX;

  newState.alliances.push({
    competitorId,
    formedAt: new Date().toISOString(),
    benefits: Object.keys(ALLIANCE_BENEFITS)
  });

  // Penalty with their enemies (competitors at same airports)
  for (const [otherId, otherComp] of Object.entries(newState.competitors)) {
    if (otherId === competitorId || !otherComp.isActive) continue;

    const sharedAirports = Object.keys(competitor.slots).filter(
      apt => otherComp.slots[apt]
    );

    if (sharedAirports.length > 0) {
      const otherRel = newState.playerRelations.competitors[otherId];
      if (otherRel) {
        otherRel.current = clamp(otherRel.current - 20, RELATIONS_MIN, RELATIONS_MAX);
        otherRel.history.push({
          event: 'ALLIANCE_WITH_THEIR_ENEMY',
          delta: -20,
          timestamp: new Date().toISOString(),
          details: { alliedWith: competitorId }
        });
      }
    }
  }

  newState.worldEvents.push({
    type: 'ALLIANCE_FORMED',
    competitorId,
    competitorName: competitor.name,
    timestamp: new Date().toISOString()
  });

  return { success: true, state: newState };
}

/**
 * Hire a negotiator
 */
export function hireNegotiator(state, tier = 1) {
  const tiers = {
    1: { monthlyFee: 50000, bonus: 5, name: "Junior Negotiator" },
    2: { monthlyFee: 100000, bonus: 10, name: "Senior Negotiator" },
    3: { monthlyFee: 200000, bonus: 15, name: "Executive Negotiator" }
  };

  const negotiator = tiers[tier];
  if (!negotiator) return state;

  const newState = deepClone(state);
  newState.negotiator = {
    hired: true,
    tier,
    ...negotiator,
    hiredAt: new Date().toISOString()
  };

  return newState;
}

/**
 * Fire negotiator
 */
export function fireNegotiator(state) {
  const newState = deepClone(state);
  newState.negotiator = null;
  return newState;
}

/**
 * Rebrand airline (costs $2M, resets negative relations)
 */
export function rebrandAirline(state) {
  const newState = deepClone(state);

  // Reset all negative airport relations to 0
  for (const [code, rel] of Object.entries(newState.playerRelations.airports)) {
    if (rel.current < 0) {
      newState.playerRelations.airports[code] = {
        ...rel,
        current: 0,
        history: [...rel.history, {
          event: 'REBRAND',
          delta: 'reset',
          timestamp: new Date().toISOString()
        }]
      };
    }
  }

  // Reset all negative competitor relations to 0
  for (const [id, rel] of Object.entries(newState.playerRelations.competitors)) {
    if (rel.current < 0 && !rel.isAllied) {
      newState.playerRelations.competitors[id] = {
        ...rel,
        current: 0,
        history: [...rel.history, {
          event: 'REBRAND',
          delta: 'reset',
          timestamp: new Date().toISOString()
        }]
      };
    }
  }

  newState.rebrands++;

  newState.worldEvents.push({
    type: 'PLAYER_REBRAND',
    rebrandCount: newState.rebrands,
    timestamp: new Date().toISOString()
  });

  return newState;
}

/**
 * Run ad campaign at airport
 */
export function runAdCampaign(state, airportCode, cost = 200000) {
  return recordAirportRelationsEvent(state, airportCode, 'AD_CAMPAIGN', { cost });
}

// ============================================================================
// QUERY FUNCTIONS
// ============================================================================

/**
 * Get competitor info (live state)
 */
export function getCompetitor(state, competitorId) {
  return state.competitors[competitorId] || null;
}

/**
 * Get airport info (live state)
 */
export function getAirport(state, airportCode) {
  return state.airports[airportCode] || null;
}

/**
 * Get all active competitors at an airport
 */
export function getCompetitorsAtAirport(state, airportCode) {
  const result = [];
  for (const [id, competitor] of Object.entries(state.competitors)) {
    if (competitor.isActive && competitor.slots[airportCode]) {
      result.push({
        id,
        ...competitor,
        slotsHere: competitor.slots[airportCode]
      });
    }
  }
  return result;
}

/**
 * Get available slots at an airport
 */
export function getAvailableSlots(state, airportCode, playerSlots = 0) {
  const airport = state.airports[airportCode];
  if (!airport) return 0;

  const competitorSlots = Object.values(state.competitors)
    .filter(c => c.isActive)
    .reduce((sum, c) => sum + (c.slots[airportCode] || 0), 0);

  return Math.max(0, airport.totalSlots - competitorSlots - playerSlots);
}

/**
 * Get world events (filtered by type if specified)
 */
export function getWorldEvents(state, eventType = null, limit = 50) {
  let events = state.worldEvents || [];
  if (eventType) {
    events = events.filter(e => e.type === eventType);
  }
  return events.slice(-limit);
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  COMPETITOR_TEMPLATES,
  AIRPORT_TEMPLATES,
  RELATIONS_THRESHOLDS,
  NEGOTIATION_RESPONSES,
  AIRPORT_RELATIONS_EVENTS,
  COMPETITOR_RELATIONS_EVENTS,
  ALLIANCE_BENEFITS
};

export default {
  createRelationsState,
  // World mutations
  competitorExpandsTo,
  competitorLosesSlot,
  competitorGoesBankrupt,
  newCompetitorEnters,
  airportShiftsLoyalty,
  updateAirportDemand,
  competitorMerge,
  updateCompetitorHealth,
  expandAirport,
  updateAirportCongestion,
  // Player relations
  getEffectiveAirportRelations,
  getNegotiationResponse,
  recordAirportRelationsEvent,
  recordCompetitorRelationsEvent,
  canFormAlliance,
  formAlliance,
  hireNegotiator,
  fireNegotiator,
  rebrandAirline,
  runAdCampaign,
  // Queries
  getCompetitor,
  getAirport,
  getCompetitorsAtAirport,
  getAvailableSlots,
  getWorldEvents
};
